import clsx from 'clsx';
import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { GroupedVirtuosoHandle } from 'react-virtuoso';
import { PageSection } from 'src/components/common/PageSection';
import { TimeDistance } from 'src/components/common/TimeDistance';
import { BoostListingButton } from 'src/components/SponsoredListings/BoostListingButton';
import { CampaignTabBar } from 'src/components/SponsoredListings/CampaignTabBar';
import { useContent } from 'src/contexts/ContentContext';
import { SelectionMode } from 'src/contexts/MultiSelectionContext';
import { vars } from 'src/core/themes';
import { Stack } from 'src/core/ui';
import { useEventLastReviewed } from 'src/hooks/useEventLastReviewed';
import { useGetEventFullInfo } from 'src/hooks/useGetEventFullInfo';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { createInventoryEventUrl } from 'src/navigations/Routes/InventoryEvent/InventoryEvent.utils';
import { ChevronDownIcon, ChevronUpIcon } from 'src/svgs/Viagogo';
import { MobileEventTile } from 'src/tiles/EventTile';
import { EventTileListingMetrics } from 'src/tiles/EventTile/MobileEventTile.components';
import * as tileStyles from 'src/tiles/EventTile/MobileEventTile.css';
import { ContentId } from 'src/utils/constants/contentId';
import { stringToUtcDate } from 'src/utils/dateTimeUtils';
import { getQuantityNumberFormat } from 'src/utils/format';
import { Event, Feature, ListingMetrics } from 'src/WebApiController';

import * as styles from './EventGroupedList.css';

export const EventTilesGroupHeader = ({
  index,
  groupName,
  groupCount,
  virtuosoHandler,
  collapsed,
  updateCollapse,
}: {
  index: number;
  groupName: string;
  groupCount: number;
  virtuosoHandler?: GroupedVirtuosoHandle | null;
  collapsed: boolean;
  updateCollapse: (collapsed: boolean) => void;
}) => {
  const eventText = useContent(ContentId.Event);
  const eventsText = useContent(ContentId.Events);

  const eventCountText = useMemo(() => {
    if (groupCount === 1) {
      return `${groupCount} ${eventText}`;
    }
    return `${getQuantityNumberFormat().format(groupCount)} ${eventsText}`;
  }, [eventText, eventsText, groupCount]);

  return (
    <PageSection.Header
      className={styles.virtuosoGroupHeader}
      onClick={() => {
        virtuosoHandler?.scrollToIndex({ groupIndex: index });
        updateCollapse(!collapsed);
      }}
      action={
        collapsed ? (
          <ChevronDownIcon size={vars.iconSize.s} />
        ) : (
          <ChevronUpIcon size={vars.iconSize.s} />
        )
      }
    >
      <Stack direction="column" justifyContent="center">
        <span>{groupName}</span>
        <span className={styles.virtuosoGroupHeaderSubtitle}>
          {eventCountText}
        </span>
      </Stack>
    </PageSection.Header>
  );
};

export const EventTilesGroupItem = ({
  event,
  metrics,
  selectionMode,
}: {
  event: Event;
  metrics?: ListingMetrics;
  selectionMode?: SelectionMode;
}) => {
  const navigate = useNavigate();
  const { venue, performer } = useGetEventFullInfo(event);
  const { eventLastReviewed } = useEventLastReviewed();

  const hasMultiCampaignsFeature = useUserHasFeature(
    Feature.SponsoredListingsV2_MultiCampaignsSupport
  );

  const lastReviewedDateTime = event?.virtId
    ? eventLastReviewed[event.virtId]
    : undefined;

  const lastReviewedText = useContent(ContentId.LastReviewed);

  const onEventClick = useCallback(() => {
    const eventUrl = createInventoryEventUrl(event.virtId, true);
    navigate(eventUrl);
  }, [event.virtId, navigate]);

  const boostAction = !hasMultiCampaignsFeature ? (
    <BoostListingButton event={event} compact />
  ) : null;

  const campaignTabBar = hasMultiCampaignsFeature ? (
    <CampaignTabBar event={event} compact />
  ) : null;

  const lastReviewedDisplay = lastReviewedDateTime ? (
    <Stack
      alignItems="center"
      justifyContent="center"
      gap="s"
      className={clsx(
        tileStyles.eventHeaderMetricsContainer,
        tileStyles.eventHeaderMetricsBroadcast
      )}
    >
      <span className={tileStyles.eventHeaderMetricsProfitAndLossLabel}>
        {lastReviewedText}
      </span>
      <TimeDistance
        date={stringToUtcDate(lastReviewedDateTime)}
        frequency={60_000}
      />
    </Stack>
  ) : null;

  const metricsNode = (
    <Stack alignItems="center" gap="m" style={{ flexWrap: 'wrap' }}>
      {metrics && <EventTileListingMetrics metrics={metrics} />}
      {lastReviewedDisplay}
    </Stack>
  );

  return (
    <div className={styles.virtuosoGroupItemContainer}>
      <MobileEventTile
        event={event}
        venue={venue}
        performer={performer}
        metrics={metricsNode}
        boostAction={boostAction}
        campaignTabBar={campaignTabBar}
        selectionMode={selectionMode}
        onClick={onEventClick}
      />
    </div>
  );
};
